import { useCallback } from "react";
import { safeExoticStake } from "@/utilities/exoticStake";
import { useBetslipStore } from "@/features/betslip/stores/betslipStore";
import { useRemoveSelection } from "@/features/betslip/hooks/useBetslipActions";
import { useSetSingleStake } from "@/features/betslip/hooks/useStakeActions";
import { useIsPermittedToBet } from "@/features/betslip/hooks/useIsPermittedToBet";
import { useIsLoggedIn } from "@/features/auth/hooks/useIsLoggedIn";
import { computeSelectionAlert } from "@/features/betslip/utils/alerts";
import { useGameSessions } from "@/features/casino/hooks/useGameSessions";
import { useShellStore } from "@/features/shell/stores/shellStore";
import { useShareEntryModal } from "@/features/entries/hooks/useShareEntryModal";
import { useEntry } from "hooks/firestore/betting/useEntries";
import { useFiatCurrency } from "hooks/firestore/user/useFiatCurrency";
import type { Alert, AlertLevel } from "@/features/betslip/types";

export const useSelectionAlert = (outcomeId: string, placement: AlertLevel) => {
  const selection = useBetslipStore((state) => state.selections[outcomeId]);
  const result = useBetslipStore((state) => state.selectionResults[outcomeId]);
  const pendingOdds = useBetslipStore((state) => state.oddsChanges[outcomeId]);

  const marketStatus = useBetslipStore(
    (state) => state.effectiveMarketStatuses[outcomeId],
  );

  const submissionPhase = useBetslipStore((state) => state.submission.phase);
  // Only the intercepted bets' own cards show "under review"; other
  // cards keep showing their own results.
  const isIntercepted = useBetslipStore(
    (state) =>
      state.overlay.type === "intercepted" &&
      state.overlay.outcomeIds.includes(outcomeId),
  );
  const isDelayed = useBetslipStore(
    (state) =>
      state.overlay.type === "delayed" &&
      state.overlay.outcomeIds.includes(outcomeId),
  );
  const isLiveViewOnly = useBetslipStore(
    (state) => state.liveViewOnly[outcomeId] && state.hasSession,
  );
  const acceptOddsChange = useBetslipStore((state) => state.acceptOddsChange);
  const setOverlay = useBetslipStore((state) => state.setOverlay);
  const dismissSelectionResult = useBetslipStore(
    (state) => state.dismissSelectionResult,
  );
  const removeSelection = useRemoveSelection();
  const setSingleStake = useSetSingleStake();
  const setModal = useShellStore((state) => state.setModal);
  const { hasActiveGameSession } = useGameSessions(true);
  const [setShareOpen, getShareModalProps] = useShareEntryModal();
  const isLoggedIn = useIsLoggedIn();
  const currencyType = useFiatCurrency();
  const isPermitted = useIsPermittedToBet(outcomeId);

  // Share rides only with the card-level (accepted) instance; the outcome
  // instance passes no id, so it never opens a subscription.
  const shareReferenceId =
    placement === "card" && result?.status === "accepted"
      ? result.betId
      : undefined;
  const { entry, entryLoading } = useEntry({ entryId: shareReferenceId ?? "" });

  const onCta = useCallback(
    (cta: NonNullable<Alert["cta"]>) => {
      switch (cta.action) {
        case "acceptOdds":
          acceptOddsChange(outcomeId);
          break;
        case "deposit":
          setOverlay({ type: "deposit" });
          break;
        case "removeSelection":
          removeSelection(outcomeId);
          break;
        case "updateStake": {
          const payload = cta.payload as
            | { alternativeStake?: number }
            | undefined;
          if (payload?.alternativeStake !== undefined) {
            // Exotic stakes must divide evenly across combinations, so
            // the offered amount snaps down to the nearest safe value.
            const snapped =
              selection?.kind === "exotic" && selection.combinations > 0
                ? Math.round(
                    safeExoticStake(
                      payload.alternativeStake / 100,
                      selection.combinations,
                    ) * 100,
                  )
                : payload.alternativeStake;
            setSingleStake(outcomeId, snapped);
          }
          break;
        }
        case "dismiss":
          dismissSelectionResult(outcomeId);
          break;
        case "endSession":
          setModal("endGameSession");
          break;
        case "share":
          setShareOpen(true);
          break;
      }
    },
    [
      outcomeId,
      selection,
      acceptOddsChange,
      setOverlay,
      removeSelection,
      setSingleStake,
      dismissSelectionResult,
      setModal,
      setShareOpen,
    ],
  );

  const computed = selection
    ? computeSelectionAlert({
        result: result ?? undefined,
        pendingOdds,
        marketStatus,
        isIntercepted,
        isDelayed,
        isSubmitting: submissionPhase === "submitting",
        isExotic: selection.kind === "exotic",
        hasActiveGameSession,
        isNotPermitted: isLoggedIn && !isPermitted,
        isLiveViewOnly,
        currencyType,
        canShare: !!entry,
      })
    : null;

  const alert =
    computed && (computed.level ?? "outcome") === placement ? computed : null;

  const shareEntryProps = shareReferenceId
    ? getShareModalProps({
        entry: entry ? [entry, entryLoading] : undefined,
        referenceId: shareReferenceId,
      })
    : null;

  return { alert, onCta, shareEntryProps };
};
